import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useForm from '@afosto/utils-react/validation/useForm';
import useIntl from '@afosto/utils-react/intl/useIntl';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Autocomplete, {
  createFilterOptions,
  highlightSubstring,
} from '@afosto/app-components-react/Autocomplete';
import Form from '@afosto/app-components-react/Form';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import OrganisationSearch from '@afosto/app-components-react/OrganisationSearch';
import TextField from '@afosto/app-components-react/TextField';
import translations from './translations';
import validationSchema from './validations';

const FulfilmentLocationForm = ({ formRef, location, inline, onSubmit, onCancel }) => {
  const intl = useIntl();
  const [addressOptions, setAddressOptions] = useState([]);

  const form = useForm({
    validationSchema,
    editInline: inline,
    defaultValues: {
      name: location?.name ?? '',
      organisation: '',
      address: location.address ?? location?.address_id,
    },
    onSubmit,
    onCancel,
  });

  const { watch, setValue } = form;
  const organisationValue = watch('organisation');

  const renderAddressOption = (option, { inputValue = '' } = {}) => {
    let optionLabel = option;

    if (typeof option === 'object') {
      // eslint-disable-next-line prefer-destructuring
      optionLabel = option.label;
    }
    return (
      <ListItem component="div" dense disableGutters>
        <ListItemText
          primary={highlightSubstring(optionLabel, inputValue)}
          secondary={highlightSubstring(option.description, inputValue)}
        />
      </ListItem>
    );
  };

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: option => `${option.label} ${option.description ?? ''}`,
  });

  useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    formRef.current = form;
  });

  useEffect(() => {
    let isMounted = true;

    const billingAddresses = [
      ...(organisationValue?.billing?.primary ? [organisationValue?.billing?.primary] : []),
      ...(organisationValue?.billing?.secondary ?? []),
    ];
    const shippingAddresses = [
      ...(organisationValue?.shipping?.primary ? [organisationValue?.shipping?.primary] : []),
      ...(organisationValue?.shipping?.secondary ?? []),
    ];

    const addresses = [...billingAddresses, ...shippingAddresses];

    if (addresses.length > 0 && isMounted) {
      setAddressOptions(
        addresses?.reduce(
          (acc, address) => [
            ...acc,
            {
              ...address,
              value: address?.id,
              label: `${address?.address_line_1}, ${address?.postal_code} ${address?.locality} ${address?.country_code}`,
              description: `${address?.given_name}${
                address?.additional_name ? ` ${address?.additional_name}` : ''
              } ${address?.family_name}${
                address?.organisation ? `, ${address?.organisation}` : ''
              }`,
            },
          ],
          [],
        ),
      );
    } else if (isMounted) {
      setAddressOptions([]);
    }
    if (!!organisationValue && organisationValue !== '') {
      setValue('address', null);
    }

    return () => {
      isMounted = false;
    };
  }, [setValue, organisationValue]);

  return (
    <LoadingWrapper isLoading={form.isSubmitting}>
      <Form form={form}>
        <TextField
          name="name"
          label={intl.formatMessage(translations.fields.name)}
          readOnly={form.editInline}
        />
        {(form?.isEditing || !inline) && <OrganisationSearch name="organisation" />}
        <Autocomplete
          name="address"
          options={addressOptions}
          label={intl.formatMessage(translations.fields.address)}
          renderOption={renderAddressOption}
          filterOptions={filterOptions}
          readOnly={form.editInline}
        />
      </Form>
    </LoadingWrapper>
  );
};

FulfilmentLocationForm.propTypes = {
  formRef: PropTypes.object,
  location: PropTypes.object,
  inline: PropTypes.bool,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
};

FulfilmentLocationForm.defaultProps = {
  formRef: { current: null },
  location: {},
  inline: false,
  onSubmit: undefined,
  onCancel: undefined,
};

export default FulfilmentLocationForm;
